/* eslint-disable import/no-named-as-default */

// ConfigurePage.js
import React, { useState, useCallback, useEffect, useMemo, useRef } from 'react'
import PropTypes from 'prop-types'
import { useNavigate } from 'react-router-dom'
import update from 'react-addons-update'
import moment from 'moment'
import statusCalculate from 'altrac-application-status'
import { injectIntl } from 'react-intl'
import {
  IconLink,
} from '../Navigation/Buttons'
import { Default } from '../../../models/ConfigureModels'
import ConfigureButtons from './ConfigureButtons'
import TelemetryButtons from './TelemetryButtons'
import PhysicalButtons from './PhysicalButtons'
import Headline from './Headline'
import { ChartBinDicator } from '../TelemetryComponents/TelemetryBinDicators'
import ChartGeneric from '../TelemetryComponents/ChartGeneric'
import ChartPump from '../TelemetryComponents/ChartPump'
import ChartPump2 from '../TelemetryComponents/ChartPump2'
import { ChartStats } from '../TelemetryComponents/TelemetryStats'
import { ChartFlow } from '../TelemetryComponents/TelemetryFlow'
import ChartWindMachine from '../TelemetryComponents/ChartWindMachine'

import Semios from '../Graphics/Semios'

import navStyles from '../../../../css/navigation.module.css'
import deviceStyles from '../../../../css/devicePage.module.css'

import AppDispatcher from '../../../dispatchers/AppDispatcher'

const ConfigurePage = ({
  device = {},
  data,
  intl = {}
}) => {
  const {
    interface: intf,
    interface_versioned: intfVersioned,
    physical: { deviceType } = {}
  } = device
  const { formatMessage } = intl
  const navigate = useNavigate()
  const intervalRef = useRef(null)

  const interfaces = (intfVersioned && intfVersioned.data) || intf
  const {
    primaryPage: {
      chart: { offsetMultiplier, type: chartType } = {},
    } = {},
  } = interfaces || {}

  let type = 'default'

  if (deviceType === 'flow') {
    type = 'flow'
  } else if (chartType === 'stat') {
    type = 'stat'
  } else if (offsetMultiplier === 'weeks') {
    type = 'weeks'
  }

  const [offsetMultiplierState, setOffsetMultiplierState] = useState(0)
  const [typeState, setTypeState] = useState(type)
  const [chartLoading, setChartLoading] = useState(false)

  const getNewData = useCallback((inputAddress) => {
    const {
      address: propsAddress,
      interface: deviceIntf,
      interface_versioned: deviceIntfVersioned,
      physical: { deviceType: deviceDeviceType },
    } = device || {}

    const address = inputAddress || propsAddress

    const deviceInterfaces = (deviceIntfVersioned && deviceIntfVersioned.data) || deviceIntf
    const {
      primaryPage: {
        chart: {
          offsetMultiplier: chartOffsetMultiplier,
          type: chartType,
          valueKey: chartValueKey,
        } = {},
      } = {},
    } = deviceInterfaces || {}

    const dateFormat = 'YYYY-MM-DD'

    if (deviceDeviceType === 'flow') {
      AppDispatcher.dispatch({
        action: 'get-stats-flow-address',
        address,
        timeBeg: moment()
          .endOf('day')
          .subtract(1 + offsetMultiplierState, 'weeks')
          .add(1, 'days')
          .format(dateFormat),
        timeEnd: moment()
          .endOf('day')
          .subtract(offsetMultiplierState, 'weeks')
          .format(dateFormat),
      })
    } else if (chartType === 'stats') {
      const valueKey = chartValueKey || 'mean'
      AppDispatcher.dispatch({
        action: 'get-stats-address',
        address,
        timeBeg: moment()
          .endOf('day')
          .subtract(1 + offsetMultiplierState, 'weeks')
          .add(1, 'days')
          .format(dateFormat),
        timeEnd: moment()
          .endOf('day')
          .subtract(offsetMultiplierState, 'weeks')
          .format(dateFormat),
        valueKey,
      })
    } else if (offsetMultiplier === 'weeks') {
      AppDispatcher.dispatch({
        action: 'get-metric-address',
        address,
        timeBeg: moment()
          .subtract(1 + offsetMultiplierState, chartOffsetMultiplier)
          .toISOString(),
        timeEnd: moment()
          .subtract(offsetMultiplierState, chartOffsetMultiplier)
          .add(10, 'minutes')
          .toISOString(),
      })
    } else {
      AppDispatcher.dispatch({
        action: 'get-metric-address',
        address,
        timeBeg: moment()
          .subtract(1 + offsetMultiplierState, 'days')
          .toISOString(),
        timeEnd: moment()
          .subtract(offsetMultiplierState, 'days')
          .add(10, 'minutes')
          .toISOString(),
      })
    }
  }, [device, offsetMultiplierState])

  const statusUpdate = useCallback((deviceParam) => {
    const status = statusCalculate(deviceParam, intl.locale)

    AppDispatcher.dispatch({
      action: 'load-device-status',
      status,
    })
  }, [intl.locale])

  const forwardTime = useCallback(() => {
    setChartLoading(true)
    const newOffsetMultiplier = offsetMultiplierState - 1
    setOffsetMultiplierState(newOffsetMultiplier)
    getNewData()
  }, [offsetMultiplierState, getNewData])

  const backwardTime = useCallback(() => {
    setChartLoading(true)
    const newOffsetMultiplier = offsetMultiplierState + 1
    setOffsetMultiplierState(newOffsetMultiplier)
    getNewData()
  }, [offsetMultiplierState, getNewData])

  const moreDetail = useCallback((link) => {
    navigate(link)
  }, [navigate])

  useEffect(() => {
    getNewData()
  }, [getNewData])

  useEffect(() => {
    const { address } = device || {}

    intervalRef.current = setInterval(getNewData, 30000)
    statusUpdate(device)

    const setting = {
      address,
      settings: {},
      status: 'new',
    }

    AppDispatcher.dispatch({
      action: 'change-setting',
      setting,
    })

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current)
      }
    }
  }, [device, getNewData, statusUpdate])

  const {
    customer_id: customerID,
    id: deviceID,
    interface: deviceIntf = {},
    interface_versioned: deviceIntfVersioned = {},
    physical,
  } = device || {}

  const {
    customer: {
      data: customer,
    },
    metric,
    metricReady,
    stats,
    statsReady,
    flowStats,
    flowReady,
  } = data || {}

  const previousMetricRef = useRef(metric)

  useEffect(() => {
    if (chartLoading && metricReady && metric !== previousMetricRef.current) {
      setChartLoading(false)
    }
    previousMetricRef.current = metric
  }, [chartLoading, metric, metricReady])

  const basePath = `/${device.customer_id}/devices/${device.id}/`
  const settingPath = `${basePath}settings/`
  const alertPath = `${basePath}alert/`
  const dataPath = `${basePath}telemetry/`

  let primaryPage
  let other

  if (physical) {
    if (deviceIntfVersioned && typeof deviceIntfVersioned.data === 'object') {
      primaryPage = deviceIntfVersioned.data.primaryPage
      if (typeof deviceIntfVersioned.data.other === 'object') {
        other = deviceIntfVersioned.data.other
      }
    } else if (deviceIntf !== null && typeof deviceIntf === 'object') {
      primaryPage = deviceIntf.primaryPage
      if (typeof deviceIntf.other === 'object') {
        other = deviceIntf.other
      }
    } else {
      primaryPage = Default.primaryPage
      if (typeof Default.other === 'object') {
        other = Default.other
      }
    }
  }

  let moreDetailLink = `/${customerID}/devices/${deviceID}/`
  const { chart: chartObj } = primaryPage || {}
  let chart

  if (typeof chartObj === 'object') {
    const {
      type: metricType,
      chartType,
      formula,
      label,
      multiplier,
      precision,
      stacked,
      statType,
      valueKey,
      vMax,
      vMin,
    } = chartObj

    if (metricType === 'stats') {
      chart = (
        <div className={deviceStyles.chart}>
          <ChartStats
            statsData={stats}
            statsReady={statsReady}
            physical={physical}
            statType={statType}
            formula={formula}
            label={label}
            multiplier={multiplier}
            precision={precision}
            vMax={vMax}
            vMin={vMin}
            chartType={chartType}
          />
        </div>
      )
      moreDetailLink += 'telemetry/stats?'
      moreDetailLink += statType ? `statType=${statType}` : ''
      moreDetailLink += '&period=7'
    } else if (metricType === 'flow') {
      chart = (
        <div className={deviceStyles.chart}>
          <ChartFlow
            statsData={flowStats}
            statsReady={flowReady}
            physical={physical}
            statType={statType}
            formula={formula}
            label={label}
            multiplier={multiplier}
            precision={precision}
            vMax={vMax}
            vMin={vMin}
            chartType={chartType}
          />
        </div>
      )
      moreDetailLink += 'telemetry/flow?'
      moreDetailLink += statType ? `statType=${statType}` : ''
      moreDetailLink += '&period=7'
    } else {
      chart = (
        <div className={deviceStyles.chart}>
          <ChartGeneric
            sensorData={metric}
            metricReady={metricReady}
            device={device}
            dataType={valueKey}
            conversion={formula}
            precision={precision}
            dataName={label}
            multiplier={multiplier}
            vMax={vMax}
            vMin={vMin}
            stacked={stacked}
            chartType={chartType}
          />
        </div>
      )
      moreDetailLink += 'telemetry/generic?'
    }

    /**
     * Concatenates an URL parameter to the end of moreDetailLink
     * if it has a value.
     * @param {string} name of parameter to add
     */
    const addUrlParam = (name) => {
      const value = chartObj[name]
      moreDetailLink += value ? `&${name}=${value}` : ''
    };

    [
      // add parameters to moreDetalLink
      'chartType',
      'formula',
      'label',
      'multiplier',
      'precision',
      'stacked',
      'valueKey',
      'vMax',
      'vMin',
    ].forEach(addUrlParam)
  } else {
    switch (primaryPage.chart) {
      case 'bins':
        chart = (
          <div className={deviceStyles.chart}>
            <ChartBinDicator sensorData={metric} metricReady={metricReady} device={device} />
          </div>
        )
        moreDetailLink += 'telemetry/binDicator'
        break

      case 'temperature':
      case 'temperature128':
        chart = (
          <div className={deviceStyles.chart}>
            <ChartGeneric
              sensorData={metric}
              metricReady={metricReady}
              device={device}
              dataType='128'
              precision='1'
              conversion='temperature'
              dataName={formatMessage({
                id: 'sensor.temperature.ambient.short',
                defaultMessage: 'Temperature',
                description: '',
              })}
              multiplier='1'
            />
          </div>
        )
        moreDetailLink +=
          'telemetry/generic?valueKey=128&multiplier=1&formula=temperature&label=Temperature&precision=1'
        break

      case 'valve':
        chart = (
          <div className={deviceStyles.chart}>
            <ChartGeneric
              sensorData={metric}
              metricReady={metricReady}
              device={device}
              dataType='131'
              conversion='valveStatus'
              dataName={formatMessage({
                id: 'sensor.valve.status',
                defaultMessage: 'Valve Status',
                description: '',
              })}
              multiplier='1'
            />
          </div>
        )
        moreDetailLink +=
          'telemetry/generic?valueKey=131&multiplier=1&formula=valveStatus&label=Valve+Status'
        break

      case 'pump':
        chart = (
          <div className={deviceStyles.chart}>
            <ChartPump sensorData={metric} metricReady={metricReady} device={device} />
          </div>
        )
        moreDetailLink += 'telemetry/pump'
        break
      case 'pump2':
        chart = (
          <div className={deviceStyles.chart}>
            <ChartPump2 sensorData={metric} metricReady={metricReady} device={device} />
          </div>
        )
        moreDetailLink += 'telemetry/pump2'
        break

      case 'windMachine':
      case 'coldAirDrain':
      case 'pumpFrostWater':
        chart = (
          <div className={deviceStyles.chart}>
            <ChartWindMachine
              sensorData={metric}
              metricReady={metricReady}
              device={device}
              dataType='134'
              isLoading={chartLoading}
            />
          </div>
        )
        moreDetailLink += `telemetry/${primaryPage.chart}?dataType=134`
        break

      default:
        chart = (
          <div>
            <p style={{ color: 'red', fontSize: 'bigger', fontWeight: 'bold' }}>
              ERROR - CHART TYPE NOT FOUND
            </p>
          </div>
        )
    }
  }

  const chartDateLabel = useMemo(() => {
    const dateFormat = 'MMM D, YYYY'
    if (typeState === 'weeks' || typeState === 'stat' || typeState === 'flow') {
      const periodEnd = moment().endOf('day').subtract(offsetMultiplierState, 'weeks')
      const periodStart = moment().endOf('day').subtract(1 + offsetMultiplierState, 'weeks').add(1, 'days')
      return `${periodStart.format(dateFormat)} - ${periodEnd.format(dateFormat)}`
    }
    const periodEnd = moment().subtract(offsetMultiplierState, 'days')
    return periodEnd.format(dateFormat)
  }, [offsetMultiplierState, typeState])

  let chartControls
  if (primaryPage.chart) {
    chartControls = (
      <ChangeChartTime
        forwardTime={forwardTime}
        backwardTime={backwardTime}
        moreDetail={() => moreDetail(moreDetailLink)}
        currentRangeLabel={chartDateLabel}
        timeChange={(typeState === 'default'
          ? formatMessage({
            id: 'chart.oneDay',
            defaultMessage: '1 Day',
            description: 'chart 1 day',
          })
          : formatMessage({
            id: 'chart.oneWeek',
            defaultMessage: '1 Week',
            description: 'chart 1 week',
          })
        ).toUpperCase()}
      />
    )
  }

  const {
    customer: { data: customerData } = {},
    deviceStatus,
    deviceStatus: { deviceHeadlineDanger, deviceHeadlineWarning } = {},
    pinCode,
    statsLookup,
    user: { permissions: { level: userLevel } = {}, locale: { tempConv } = {} } = {},
  } = data || {}

  const { pageContainer, chartContainer } = deviceStyles
  const {
    application_settings: applicationSettings = { settings: {} },
    application_settings_new: applicationSettingsNew = { settings: {} },
    interface_versioned: interfaceVersioned = {},
    reading0 = {},
  } = device || {}

  const { page = {} } = other || {}

  return (
    <div className={pageContainer}>
      <div className={chartContainer}>
        {chartControls}
        <div onClick={() => moreDetail(moreDetailLink)}>
          {chart}
        </div>
      </div>
      <Headline
        info={deviceHeadlineDanger}
        prepend={formatMessage({
          id: 'headline.error',
          defaultMessage: 'ERROR: ',
          description: '',
        })}
      />
      <Headline
        info={deviceHeadlineWarning}
        prepend={formatMessage({
          id: 'headline.warning',
          defaultMessage: 'WARNING: ',
          description: '',
        })}
      />
      <TelemetryButtons
        device={device}
        status={deviceStatus}
        path={basePath}
        telemetryTypes={primaryPage.telemetryTypes}
        statsLookup={statsLookup}
        flowStats={flowStats}
        statsReady={statsReady}
        flowReady={flowReady}
        customer={customerData}
        pinCode={pinCode}
        page={page}
        physical={physical}
        level={userLevel}
        tempConv={tempConv}
      />
      <ConfigureButtons
        applicationSettings={applicationSettings}
        applicationSettingsNew={applicationSettingsNew}
        reading0={reading0}
        physical={physical}
        interfaceVersioned={interfaceVersioned}
        customer={customer}
        pinCode={pinCode}
        path={settingPath}
        settingTypes={primaryPage.settingTypes}
      />
      <PhysicalButtons
        device={device}
        path={settingPath}
        physicalTypes={primaryPage.physicalTypes}
      />
    </div>
  )
}

ConfigurePage.propTypes = {
  device: PropTypes.object,
  data: PropTypes.object,
  intl: PropTypes.shape({
    formatMessage: PropTypes.func,
    locale: PropTypes.string,
  }),
}

export default injectIntl(ConfigurePage)

function ChangeChartTime ({
  forwardTime, backwardTime, moreDetail, timeChange, currentRangeLabel,
}) {
  const {
    centerNavChart, hideSmall, leftNavChart, navBarChart, rightNavChart,
  } = navStyles

  return (
    <div className={navBarChart} role='group'>
      <button className={`${leftNavChart}`} onClick={backwardTime}>
        <i className='fa fa-caret-left' />
        &nbsp;
        <h5 className={hideSmall}>{timeChange}</h5>
      </button>
      <button className={centerNavChart} onClick={moreDetail}>
        <h5 className={hideSmall}>{currentRangeLabel}</h5>
      </button>
      <button className={`${rightNavChart}`} onClick={forwardTime}>
        <h5 className={hideSmall}>{timeChange}</h5>
        &nbsp;
        <i className='fa fa-caret-right' />
      </button>
    </div>
  )
}

ChangeChartTime.propTypes = {
  forwardTime: PropTypes.func,
  backwardTime: PropTypes.func,
  moreDetail: PropTypes.func,
  timeChange: PropTypes.string,
  currentRangeLabel: PropTypes.string,
}
